import {useMemo} from 'react';
import {Dropdown, Table, Tag} from 'antd';
import {MoreOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';

import useIsCompact from 'utils/useIsCompact';
import {
  currencyName as currencyFullName,
  formatMoney,
  prettifyEnum,
  symbolFor,
} from 'utils/currencyFormatting';
import styles from '../wallet.module.scss';

const emptyState = (
  <div className={styles.emptyState}>
    <h3>No currencies configured</h3>
    <p>Add a currency from the Create menu to get started.</p>
  </div>
);

const StatusTag = ({enabled}) => (
  <Tag className={styles.statusTag} color={enabled ? 'green' : 'red'}>
    {enabled ? 'Enabled' : 'Disabled'}
  </Tag>
);

StatusTag.propTypes = {enabled: PropTypes.bool};

/** "9 methods" reads better than nine stacked tags; the names live in the expanded row. */
const MethodCount = ({methods}) => {
  const count = methods?.length ?? 0;
  if (!count) return <span className={styles.methodNone}>None</span>;
  return (
    <span className={styles.methodCount}>
      <span className={styles.methodNumber}>{count}</span>
      <span className={styles.methodWord}>method{count === 1 ? '' : 's'}</span>
    </span>
  );
};

MethodCount.propTypes = {methods: PropTypes.array};

const MethodList = ({label, methods}) => (
  <div className={styles.expandedGroup}>
    <span className={styles.expandedLabel}>{label}</span>
    {methods?.length ? (
      <div className={styles.methodTags}>
        {methods.map(method => (
          <span className={styles.methodTag} key={method}>
            {prettifyEnum(method)}
          </span>
        ))}
      </div>
    ) : (
      <span className={styles.expandedEmpty}>No methods enabled</span>
    )}
  </div>
);

MethodList.propTypes = {label: PropTypes.string, methods: PropTypes.array};

const CurrenciesTable = ({data, loading, buildActionItems}) => {
  const isCompact = useIsCompact();

  const columns = useMemo(
    () => [
      {
        title: 'Currency',
        key: 'currency',
        fixed: 'left',
        width: 210,
        render: (_, record) => (
          <div className={styles.currencyCell}>
            <span className={styles.currencyBadge}>
              {record?.currencySymbol || symbolFor(record?.currency)}
            </span>
            <span className={styles.currencyText}>
              <span className={styles.currencyCode}>{record?.currency}</span>
              <span className={styles.currencyName}>
                {record?.currencyName || currencyFullName(record?.currency)}
              </span>
            </span>
          </div>
        ),
      },
      {
        title: 'Status',
        key: 'enabled',
        width: 110,
        render: (_, record) => <StatusTag enabled={!!record?.enabled} />,
      },
      {
        title: 'Deposit',
        key: 'deposit',
        width: 120,
        render: (_, record) => (
          <MethodCount methods={record?.deposit?.enabledPaymentMethods} />
        ),
      },
      {
        // Reads `withdrawal`, not `deposit`. The old page rendered the deposit
        // list under both headings, so EUR looked like it had 8 withdrawal
        // methods when it has 1.
        title: 'Withdrawal',
        key: 'withdrawal',
        width: 120,
        render: (_, record) => (
          <MethodCount methods={record?.withdrawal?.enabledPaymentMethods} />
        ),
      },
      {
        title: 'Swap',
        key: 'swap',
        width: 110,
        render: (_, record) => (
          <StatusTag enabled={!!record?.swap?.sourceCurrencyEnabledForSwap} />
        ),
      },
      {
        title: 'Swap limits',
        key: 'swapLimits',
        width: 160,
        align: 'right',
        render: (_, record) => {
          const min = record?.swap?.minSwapAmountInSourceCurrency;
          const max = record?.swap?.maxSwapAmountInSourceCurrency;
          if (min == null && max == null) {
            return <span className={styles.methodNone}>Not set</span>;
          }
          // Stacked rather than "min – max" on one line: a range like
          // "CFA10,000 – CFA5,000,000" is wider than any sensible column and
          // spilled over the neighbouring cell.
          return (
            <div className={styles.amountCell}>
              <span className={styles.money}>
                {formatMoney(min, record?.currency)}
              </span>
              <span className={styles.moneyMuted}>
                to {formatMoney(max, record?.currency)}
              </span>
            </div>
          );
        },
      },
      {
        title: '',
        key: 'actions',
        fixed: 'right',
        width: 56,
        render: (_, record) => {
          const items = buildActionItems(record);
          if (!items.length) return <span className={styles.noActions}>—</span>;
          return (
            <div
              className={styles.actionsCell}
              onClick={event => event.stopPropagation()}>
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{items}}>
                <button
                  type="button"
                  aria-label={`${record?.currency} actions`}
                  className={styles.actionsTrigger}>
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [buildActionItems],
  );

  const expandedRowRender = record => (
    <div className={styles.expanded}>
      <MethodList
        label="Deposit methods"
        methods={record?.deposit?.enabledPaymentMethods}
      />
      <MethodList
        label="Withdrawal methods"
        methods={record?.withdrawal?.enabledPaymentMethods}
      />
    </div>
  );

  if (isCompact) {
    return (
      <div className={styles.cardList}>
        {(data ?? []).map(record => {
          const items = buildActionItems(record);
          return (
            <div className={styles.card} key={record?.currency}>
              <div className={styles.cardHeader}>
                <div className={styles.currencyCell}>
                  <span className={styles.currencyBadge}>
                    {record?.currencySymbol || symbolFor(record?.currency)}
                  </span>
                  <span className={styles.currencyText}>
                    <span className={styles.currencyCode}>
                      {record?.currency}
                    </span>
                    <span className={styles.currencyName}>
                      {record?.currencyName ||
                        currencyFullName(record?.currency)}
                    </span>
                  </span>
                </div>
                <div className={styles.cardHeaderRight}>
                  <StatusTag enabled={!!record?.enabled} />
                  {items.length > 0 && (
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      menu={{items}}>
                      <button
                        type="button"
                        aria-label={`${record?.currency} actions`}
                        className={styles.actionsTrigger}>
                        <MoreOutlined />
                      </button>
                    </Dropdown>
                  )}
                </div>
              </div>

              <div className={styles.cardGrid}>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Deposit</span>
                  <span className={styles.cardValue}>
                    <MethodCount
                      methods={record?.deposit?.enabledPaymentMethods}
                    />
                  </span>
                </div>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Withdrawal</span>
                  <span className={styles.cardValue}>
                    <MethodCount
                      methods={record?.withdrawal?.enabledPaymentMethods}
                    />
                  </span>
                </div>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Swap</span>
                  <span>
                    <StatusTag
                      enabled={!!record?.swap?.sourceCurrencyEnabledForSwap}
                    />
                  </span>
                </div>
                <div className={styles.cardField}>
                  <span className={styles.cardLabel}>Swap limits</span>
                  <span className={styles.cardValue}>
                    {record?.swap?.minSwapAmountInSourceCurrency == null &&
                    record?.swap?.maxSwapAmountInSourceCurrency == null
                      ? 'Not set'
                      : `${formatMoney(
                          record?.swap?.minSwapAmountInSourceCurrency,
                          record?.currency,
                        )} – ${formatMoney(
                          record?.swap?.maxSwapAmountInSourceCurrency,
                          record?.currency,
                        )}`}
                  </span>
                </div>
              </div>

              <div className={styles.cardSection}>
                <MethodList
                  label="Deposit methods"
                  methods={record?.deposit?.enabledPaymentMethods}
                />
                <MethodList
                  label="Withdrawal methods"
                  methods={record?.withdrawal?.enabledPaymentMethods}
                />
              </div>
            </div>
          );
        })}
        {!loading && !(data ?? []).length && emptyState}
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <Table
        rowKey="currency"
        columns={columns}
        loading={loading}
        dataSource={data}
        pagination={false}
        scroll={{x: 930}}
        expandable={{expandedRowRender, fixed: 'left', columnWidth: 44}}
        locale={{emptyText: emptyState}}
      />
    </div>
  );
};

CurrenciesTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  buildActionItems: PropTypes.func,
};

export default CurrenciesTable;
